import './styles/main.css';
import { dataLoader, type GameData } from './utils/dataLoader';
import { DecisionEngine } from './utils/decisionEngine';
import { SearchEngine, type SearchableItem } from './utils/searchEngine';
import { StorageManager } from './utils/storage';
import type { UserProgress } from './types/UserProgress';
import type { Item, RecycleDecision } from './types/Item';
import type { Project } from './types/Project';
import type { Quest } from './types/Quest';
import { ItemCard } from './components/ItemCard';
import { ItemModal } from './components/ItemModal';
import { ZoneFilter } from './components/ZoneFilter';
import { showPvPGateModal } from './components/PvPGateModal';

class App {
  private gameData!: GameData;
  private decisionEngine!: DecisionEngine;
  private searchEngine!: SearchEngine;
  private userProgress: UserProgress;
  private allItems: SearchableItem[] = [];
  private filteredItems: SearchableItem[] = [];
  private zoneFilter!: ZoneFilter;
  private questPreviousRelations = new Map<string, string[]>();
  private showNonItemQuestNodes: boolean;
  private questSearchQuery: string = '';

  private searchInput!: HTMLInputElement;
  private itemsGrid!: HTMLElement;
  private searchDebounceTimer: number | null = null;
  private filters = {
    searchQuery: '',
    decisions: new Set<RecycleDecision>(),
    rarities: new Set<string>(),
    categories: new Map<string, 'include' | 'exclude'>(),
    zones: [] as string[],
    sortBy: 'name' as 'name' | 'value' | 'rarity' | 'weight' | 'decision' | 'type',
    sortDirection: 'asc' as 'asc' | 'desc'
  };

  constructor() {
    this.userProgress = StorageManager.loadUserProgress();
    this.showNonItemQuestNodes = StorageManager.loadQuestShowNonItem();
  }

  async init() {
    try {
      // Show loading state
      this.showLoading();

      // Load game data
      this.gameData = await dataLoader.loadGameData();

      // Migrate legacy project completion into phase-based progress once project data is loaded.
      this.migrateProjectPhaseProgress();

      // Initialize decision engine
      this.decisionEngine = new DecisionEngine(
        this.gameData.items,
        this.gameData.hideoutModules,
        this.gameData.quests,
        this.gameData.projects
      );

      // Get items with decisions
      this.allItems = this.decisionEngine.getItemsWithDecisions(this.userProgress);

      // Filter out blacklisted garbage items
      const itemBlacklist = ['refinement-1'];
      this.allItems = this.allItems.filter(item => !itemBlacklist.includes(item.id));

      // Hide items without images from the list UI.
      this.allItems = this.allItems.filter(item =>
        typeof item.imageFilename === 'string' && item.imageFilename.trim().length > 0
      );

      // Initialize search engine
      this.searchEngine = new SearchEngine(this.allItems);

      // Set filtered items to all items initially
      this.filteredItems = [...this.allItems];

      // Initialize UI
      this.initializeUI();

      // Render initial state
      this.render();

      // Update stats
      this.updateStats();

      // Update last updated time
      this.updateLastUpdated();

      // Hide loading
      this.hideLoading();

    } catch (error) {
      console.error('Failed to initialize app:', error);
      this.showError('Failed to load data. Please refresh the page.');
    }
  }

  private initializeUI() {
    // Search input with debouncing
    this.searchInput = document.getElementById('search') as HTMLInputElement;
    this.searchInput.addEventListener('input', (e) => {
      this.filters.searchQuery = (e.target as HTMLInputElement).value;

      // Clear existing timer
      if (this.searchDebounceTimer !== null) {
        clearTimeout(this.searchDebounceTimer);
      }

      // Set new timer to apply filters after 300ms of no typing
      this.searchDebounceTimer = setTimeout(() => {
        this.applyFilters();
        this.searchDebounceTimer = null;
      }, 300) as unknown as number;
    });

    // Items grid
    this.itemsGrid = document.getElementById('items-grid')!;

    // Initialize filter buttons
    this.initializeDecisionFilters();
    this.initializeRarityFilters();
    this.initializeCategoryFilter();
    this.initializeSortSelector();

    // Initialize dashboard
    this.initializeDashboard();

    // View toggle
    this.initializeViewToggle();

    // Initialize workshop tracker
    this.initializeWorkshopTracker();

    // Initialize zone filter
    this.initializeZoneFilter();

    // Initialize quest tracker controls
    this.initializeQuestTrackerToggle();

    // Initialize quest tracker
    this.initializeQuestTracker();

    // Initialize project tracker
    this.initializeProjectTracker();

    // Initialize mobile menu
    this.initializeMobileMenu();

    // Initialize sidebar toggle
    this.initializeSidebarToggle();
  }

  private initializeDecisionFilters() {
    const filterContainer = document.getElementById('decision-filter');
    if (!filterContainer) return;

    this.filters.decisions = StorageManager.loadDecisionFilters();

    const decisions: RecycleDecision[] = ['keep', 'sell_or_recycle', 'situational'];
    const labels: Record<RecycleDecision, string> = {
      keep: 'Keep',
      sell_or_recycle: 'Safe to Sell',
      situational: 'Your Call'
    };

    decisions.forEach(decision => {
      const button = document.createElement('button');
      button.className = 'filter-btn';
      button.dataset.decision = decision;
      button.textContent = labels[decision];
      button.classList.toggle('active', this.filters.decisions.has(decision));

      button.addEventListener('click', () => {
        if (this.filters.decisions.has(decision)) {
          this.filters.decisions.delete(decision);
          button.classList.remove('active');
        } else {
          this.filters.decisions.add(decision);
          button.classList.add('active');
        }
        StorageManager.saveDecisionFilters(this.filters.decisions);
        this.applyFilters();
      });

      filterContainer.appendChild(button);
    });
  }

  private initializeRarityFilters() {
    const filterContainer = document.getElementById('rarity-filter');
    if (!filterContainer) return;

    const rarities = ['common', 'uncommon', 'rare', 'epic', 'legendary'];

    rarities.forEach(rarity => {
      const button = document.createElement('button');
      button.className = `filter-btn rarity-${rarity}`;
      button.dataset.rarity = rarity;
      button.textContent = rarity.charAt(0).toUpperCase() + rarity.slice(1);

      button.addEventListener('click', () => {
        if (this.filters.rarities.has(rarity)) {
          this.filters.rarities.delete(rarity);
          button.classList.remove('active');
        } else {
          this.filters.rarities.add(rarity);
          button.classList.add('active');
        }
        this.applyFilters();
      });

      filterContainer.appendChild(button);
    });
  }

  // Normalize category names to handle case variations and similar names
  private normalizeCategory(type: string): string {
    const normalized = type.trim();
    const lower = normalized.toLowerCase();

    // Map similar/variant names to canonical forms
    const categoryMap: Record<string, string> = {
      'quick use': 'Quick Use',
      'quick_use': 'Quick Use',
      'consumable': 'Quick Use',
      'medical': 'Quick Use',
      'throwable': 'Quick Use',
      'mods': 'Modification',
      'mod': 'Modification',
      'modifications': 'Modification',
      'material': 'Basic Material',
      'quest item': 'Quest Item',
      'quest_item': 'Quest Item',
      'questitem': 'Quest Item',
    };

    return categoryMap[lower] || normalized;
  }

  // Get the normalized category for an item
  private getItemCategory(item: { type: string }): string {
    return this.normalizeCategory(item.type);
  }

  private initializeCategoryFilter() {
    const container = document.getElementById('category-filter');
    const resetBtn = document.getElementById('category-reset');
    if (!container) return;

    // Load saved category filters
    const savedFilters = StorageManager.loadCategoryFilters();
    const hasExistingSave = savedFilters.size > 0;
    this.filters.categories = savedFilters;

    // Default excludes for new users (no saved state)
    const defaultExcludes = ['Blueprint', 'Cosmetic', 'Misc'];

    // Get unique normalized categories sorted alphabetically
    const categories = [...new Set(this.allItems.map(item => this.getItemCategory(item)))].sort();

    // Create toggle buttons for each category
    categories.forEach(category => {
      const button = document.createElement('button');
      button.className = 'filter-btn category-btn';
      button.dataset.category = category;

      // Apply saved state, or default excludes for new users, or neutral
      let state: 'neutral' | 'include' | 'exclude' = 'neutral';
      if (hasExistingSave) {
        state = this.filters.categories.get(category) || 'neutral';
      } else if (defaultExcludes.includes(category)) {
        state = 'exclude';
        this.filters.categories.set(category, 'exclude');
      }
      button.dataset.state = state;
      button.textContent = category;

      button.addEventListener('click', () => {
        const currentState = button.dataset.state;
        let newState: 'neutral' | 'include' | 'exclude';

        // Cycle: neutral -> include -> exclude -> neutral
        if (currentState === 'neutral') {
          newState = 'include';
          this.filters.categories.set(category, 'include');
        } else if (currentState === 'include') {
          newState = 'exclude';
          this.filters.categories.set(category, 'exclude');
        } else {
          newState = 'neutral';
          this.filters.categories.delete(category);
        }

        button.dataset.state = newState;
        StorageManager.saveCategoryFilters(this.filters.categories);
        this.applyFilters();
      });

      container.appendChild(button);
    });

    // Reset button clears all category filters
    resetBtn?.addEventListener('click', () => {
      this.filters.categories.clear();
      StorageManager.saveCategoryFilters(this.filters.categories);
      container.querySelectorAll('.category-btn').forEach(btn => {
        (btn as HTMLElement).dataset.state = 'neutral';
      });
      this.applyFilters();
    });

    // Apply saved filters on load
    if (this.filters.categories.size > 0) {
      this.applyFilters();
    }
  }

  private initializeSortSelector() {
    const select = document.getElementById('sort-selector') as HTMLSelectElement;
    if (!select) return;

    select.addEventListener('change', (e) => {
      this.filters.sortBy = (e.target as HTMLSelectElement).value as any;
      this.applyFilters();
    });

    // Sort direction toggle
    const directionBtn = document.getElementById('sort-direction-btn');
    if (directionBtn) {
      directionBtn.addEventListener('click', () => {
        this.filters.sortDirection = this.filters.sortDirection === 'asc' ? 'desc' : 'asc';
        this.updateSortDirectionButton();
        this.applyFilters();
      });
      this.updateSortDirectionButton();
    }
  }

  private updateSortDirectionButton() {
    const btn = document.getElementById('sort-direction-btn');
    if (!btn) return;

    const isAsc = this.filters.sortDirection === 'asc';
    btn.innerHTML = isAsc ? '↑' : '↓';
    btn.title = isAsc ? 'Sort ascending' : 'Sort descending';
    btn.setAttribute('data-direction', this.filters.sortDirection);
  }

  private initializeDashboard() {
    const dashboardCards = document.querySelectorAll('.dashboard-card');

    dashboardCards.forEach(card => {
      card.addEventListener('click', () => {
        const decision = card.classList[1] as RecycleDecision; // Second class is the decision type

        // Toggle filter
        const filterBtn = document.querySelector(`[data-decision="${decision}"]`);
        if (filterBtn) {
          (filterBtn as HTMLElement).click();
        }
      });
    });
  }

  private initializeViewToggle() {
    const desktopViewBtns = document.querySelectorAll<HTMLButtonElement>('.toggle-btn[data-view]');
    const mobileViewBtns = document.querySelectorAll<HTMLButtonElement>('.mobile-view-btn[data-view]');
    const isMobile = window.innerWidth <= 768;

    const setView = (view: 'grid' | 'list' | 'compact') => {
      this.itemsGrid.classList.toggle('list-view', view === 'list');
      this.itemsGrid.classList.toggle('compact-view', view === 'compact');
      document.body.classList.toggle('compact-mode', view === 'compact');
      StorageManager.saveViewMode(view);

      desktopViewBtns.forEach(btn => {
        btn.classList.toggle('active', btn.dataset.view === view);
      });

      mobileViewBtns.forEach(btn => {
        btn.classList.toggle('active', btn.dataset.view === view);
      });
    };

    // Restore last selected view if present, otherwise default by screen size.
    const savedView = StorageManager.loadViewMode();
    if (savedView) {
      setView(savedView);
    } else if (isMobile) {
      setView('list');
    } else {
      setView('grid');
    }

    desktopViewBtns.forEach(btn => {
      btn.addEventListener('click', () => {
        const view = btn.dataset.view;
        if (view === 'grid' || view === 'list' || view === 'compact') {
          setView(view);
        }
      });
    });

    mobileViewBtns.forEach(btn => {
      btn.addEventListener('click', () => {
        const view = btn.dataset.view;
        if (view === 'grid' || view === 'list' || view === 'compact') {
          setView(view);
        }
      });
    });
  }

  private initializeSidebarToggle() {
    const btn = document.getElementById('sidebar-toggle');
    const sidebar = document.querySelector('.sidebar') as HTMLElement | null;
    const wrapper = document.querySelector('.layout-wrapper') as HTMLElement | null;
    if (!btn || !sidebar || !wrapper) return;

    const setHidden = (hidden: boolean) => {
      wrapper.classList.toggle('sidebar-hidden', hidden);
      btn.textContent = hidden ? '\u25b6' : '\u25c0';
      btn.title = hidden ? 'Show sidebar' : 'Hide sidebar';
      StorageManager.saveSidebarHidden(hidden);
    };

    // Restore persisted state
    setHidden(StorageManager.loadSidebarHidden());

    btn.addEventListener('click', () => {
      setHidden(!wrapper.classList.contains('sidebar-hidden'));
    });
  }

  private initializeMobileMenu() {
    const menuBtn = document.getElementById('mobile-menu-btn');
    const dropdown = document.getElementById('mobile-menu-dropdown');
    const filtersBtn = document.getElementById('mobile-filters-btn');
    const mobileSortSelect = document.getElementById('mobile-sort-selector') as HTMLSelectElement;
    const sidebar = document.querySelector('.sidebar');

    if (!menuBtn || !dropdown) return;

    // Toggle dropdown
    menuBtn.addEventListener('click', (e) => {
      e.stopPropagation();
      dropdown.classList.toggle('open');
    });

    // Close dropdown when clicking outside
    document.addEventListener('click', () => {
      dropdown.classList.remove('open');
    });

    dropdown.addEventListener('click', (e) => {
      e.stopPropagation();
    });

    // Filters button - open sidebar as modal
    if (filtersBtn && sidebar) {
      // Create overlay if it doesn't exist
      let overlay = document.querySelector('.sidebar-modal-overlay');
      if (!overlay) {
        overlay = document.createElement('div');
        overlay.className = 'sidebar-modal-overlay';
        document.body.appendChild(overlay);
      }

      const openModal = () => {
        sidebar.classList.add('mobile-modal');
        overlay!.classList.add('open');
        dropdown.classList.remove('open');
      };

      const closeModal = () => {
        sidebar.classList.remove('mobile-modal');
        overlay!.classList.remove('open');
      };

      filtersBtn.addEventListener('click', openModal);
      overlay.addEventListener('click', closeModal);
    }

    // Sync mobile sort with desktop
    if (mobileSortSelect) {
      const desktopSort = document.getElementById('sort-selector') as HTMLSelectElement;

      mobileSortSelect.addEventListener('change', (e) => {
        const value = (e.target as HTMLSelectElement).value;
        this.filters.sortBy = value as any;
        if (desktopSort) desktopSort.value = value;
        this.applyFilters();
      });
    }

  }

  private initializeWorkshopTracker() {
    const workshopGrid = document.getElementById('workshop-grid');
    if (!workshopGrid) return;

    // Filter out stash and workbench as they're not relevant for this tool
    const relevantModules = this.gameData.hideoutModules.filter(
      module => module.id !== 'stash' && module.id !== 'workbench'
    );

    relevantModules.forEach(module => {
      const currentLevel = this.userProgress.hideoutLevels[module.id] ?? 0;
      const moduleName = module.name;

      const getLevelText = (level: number) => {
        if (level === 0) return 'Not Unlocked';
        if (level === module.maxLevel) return `Level ${level} (Max)`;
        return `Level ${level} / ${module.maxLevel}`;
      };

      const card = document.createElement('div');
      card.className = 'workshop-card';
      card.innerHTML = `
        <h3>${moduleName}</h3>
        <div class="workshop-card__level">
          <span class="workshop-card__level-text">${getLevelText(currentLevel)}</span>
          <div class="workshop-card__ticks"></div>
        </div>
      `;

      const levelText = card.querySelector('.workshop-card__level-text') as HTMLSpanElement;
      const ticksContainer = card.querySelector('.workshop-card__ticks') as HTMLDivElement;

      // Helper to update UI and save progress
      const updateProgress = (newLevel: number) => {
        levelText.textContent = getLevelText(newLevel);

        // Update tick visual states
        ticksContainer.querySelectorAll('.workshop-card__tick').forEach((tick, idx) => {
          tick.classList.toggle('completed', idx < newLevel);
        });

        // Save progress
        this.updateWorkshopLevel(module.id, newLevel);
      };

      // Create tick marks for each level (1 to maxLevel)
      for (let level = 1; level <= module.maxLevel; level++) {
        const tick = document.createElement('div');
        tick.className = 'workshop-card__tick';
        if (level <= currentLevel) {
          tick.classList.add('completed');
        }
        tick.dataset.moduleId = module.id;
        tick.dataset.level = String(level);

        // Find level data for requirements
        const levelData = module.levels.find(l => l.level === level);

        // Add hover for popover
        tick.addEventListener('mouseenter', (e) => this.showHideoutPopover(module, level, levelData, e));
        tick.addEventListener('mouseleave', () => this.hideHideoutPopover());

        // Click to set progress
        tick.addEventListener('click', () => {
          const currentlyCompleted = tick.classList.contains('completed');
          // If clicking on a completed level, set to previous level
          // If clicking on incomplete level, complete up to and including this one
          const newLevel = currentlyCompleted ? level - 1 : level;
          updateProgress(newLevel);
        });

        ticksContainer.appendChild(tick);
      }

      workshopGrid.appendChild(card);
    });
  }

  private showHideoutPopover(module: any, level: number, levelData: any, event: MouseEvent) {
    // Remove existing popover if any
    this.hideHideoutPopover();

    const popover = document.createElement('div');
    popover.className = 'quest-popover'; // Reuse quest popover styling
    popover.id = 'hideout-popover';

    // Build requirements HTML
    let requirementsHtml = '<span class="quest-popover__none">None</span>';
    if (levelData?.requirementItemIds && levelData.requirementItemIds.length > 0) {
      requirementsHtml = levelData.requirementItemIds.map((req: any) => {
        const itemId = req.item_id || req.itemId;
        const quantity = req.quantity || '?';
        const item = this.gameData.items.find(i => i.id === itemId);
        const itemName = item?.name || itemId || 'Unknown';
        const iconUrl = item && typeof item.imageFilename === 'string' && item.imageFilename.trim().length > 0
          ? dataLoader.getIconUrl(item)
          : '';

        return `
          <div class="quest-popover__item">
            ${iconUrl ? `<img src="${iconUrl}" alt="" class="quest-popover__item-icon" />` : ''}
            <span>${quantity}x ${itemName}</span>
          </div>
        `;
      }).join('');
    }

    // Check for other requirements (like coins for stash)
    let otherReqsHtml = '';
    if (levelData?.otherRequirements && levelData.otherRequirements.length > 0) {
      otherReqsHtml = levelData.otherRequirements.map((req: string) => `
        <div class="quest-popover__item">
          <span>${req}</span>
        </div>
      `).join('');
    }

    const allRequirementsHtml = requirementsHtml + otherReqsHtml || '<span class="quest-popover__none">None</span>';

    popover.innerHTML = `
      <div class="quest-popover__title">${module.name} - Level ${level}</div>
      ${levelData?.description ? `<div class="quest-popover__desc">${levelData.description}</div>` : ''}
      <div class="quest-popover__section">
        <div class="quest-popover__section-title">Requirements</div>
        <div class="quest-popover__items">${allRequirementsHtml}</div>
      </div>
    `;

    document.body.appendChild(popover);

    // Position the popover near the tick (above it)
    const rect = (event.target as HTMLElement).getBoundingClientRect();
    const popoverRect = popover.getBoundingClientRect();

    let left = rect.left + rect.width / 2 - popoverRect.width / 2;
    let top = rect.top - popoverRect.height - 8;

    // Keep popover within viewport
    if (left < 8) left = 8;
    if (left + popoverRect.width > window.innerWidth - 8) {
      left = window.innerWidth - popoverRect.width - 8;
    }
    if (top < 8) {
      top = rect.bottom + 8;
    }

    popover.style.left = `${left}px`;
    popover.style.top = `${top}px`;
  }

  private hideHideoutPopover() {
    const existing = document.getElementById('hideout-popover');
    if (existing) {
      existing.remove();
    }
  }

  private updateWorkshopLevel(moduleId: string, level: number) {
    this.userProgress.hideoutLevels[moduleId] = level;
    StorageManager.saveUserProgress(this.userProgress);

    // Recalculate decisions
    this.allItems = this.decisionEngine.getItemsWithDecisions(this.userProgress);
    this.searchEngine.updateIndex(this.allItems);

    // Re-apply filters and render
    this.applyFilters();
    this.updateStats();
  }

  private initializeZoneFilter() {
    this.zoneFilter = new ZoneFilter({
      items: this.allItems,
      selectedZones: this.filters.zones,
      onZoneSelect: (zones: string[]) => {
        this.filters.zones = zones;
        this.applyFilters();
      }
    });

    // Mount to zone filter container in sidebar
    const zoneFilterContainer = document.getElementById('zone-filter-container');
    if (zoneFilterContainer) {
      this.zoneFilter.mount(zoneFilterContainer);
    }
  }

  private initializeQuestTracker() {
    const questTracker = document.getElementById('quest-tracker');
    if (!questTracker) return;

    questTracker.innerHTML = '';
    this.buildQuestRelations(this.gameData.quests);
    const visibleQuests = this.gameData.quests.filter((quest) => {
      const hasItemRequirements = this.hasQuestItemRequirements(quest);
      if (!this.showNonItemQuestNodes && !hasItemRequirements) {
        return false;
      }

      if (!hasItemRequirements) {
        return true;
      }

      // Keep dependency-chain quests visible even when required item images are missing,
      // but only when the non-item toggle is active.
      const hasQuestRelations =
        (Array.isArray(quest.previousQuestIds) && quest.previousQuestIds.length > 0) ||
        (Array.isArray(quest.nextQuestIds) && quest.nextQuestIds.length > 0);

      return this.hasQuestRequirementImage(quest) || (this.showNonItemQuestNodes && hasQuestRelations);
    });

    // Group quests by trader (quest giver)
    const questsByTrader = new Map<string, typeof this.gameData.quests>();
    for (const quest of visibleQuests) {
      const trader = quest.trader || 'Unknown';
      if (!questsByTrader.has(trader)) {
        questsByTrader.set(trader, []);
      }
      questsByTrader.get(trader)!.push(quest);
    }

    // Order quests within each trader by dependency relations, with sortOrder as fallback
    for (const [, quests] of questsByTrader) {
      const ordered = this.orderQuestsByRelations(quests);
      quests.splice(0, quests.length, ...ordered);
    }

    // Sort traders alphabetically, but put "Unknown" at the end
    const sortedTraders = [...questsByTrader.keys()].sort((a, b) => {
      if (a === 'Unknown') return 1;
      if (b === 'Unknown') return -1;
      return a.localeCompare(b);
    });

    // Create a card for each trader
    for (const trader of sortedTraders) {
      const quests = questsByTrader.get(trader)!;
      if (quests.length === 0) continue;

      // Count completed quests for this trader
      const completedCount = quests.filter(q =>
        this.userProgress.completedQuests.includes(q.id)
      ).length;

      const card = document.createElement('div');
      card.className = 'quest-card';

      const questCountText = completedCount === quests.length
        ? `All ${quests.length} Complete`
        : `${completedCount} / ${quests.length}`;

      card.innerHTML = `
        <h3>${trader}</h3>
        <div class="quest-card__level">
          <span class="quest-card__level-text">${questCountText}</span>
          <div class="quest-card__ticks"></div>
        </div>
      `;

      const ticksContainer = card.querySelector('.quest-card__ticks') as HTMLDivElement;

      // Create tick marks for each quest
      quests.forEach((quest, index) => {
        const tick = document.createElement('div');
        tick.className = 'quest-card__tick';
        if (!this.hasQuestItemRequirements(quest)) {
          tick.classList.add('quest-card__tick--non-item');
        }
        if (this.questMatchesSearch(quest)) {
          tick.classList.add('quest-card__tick--search-match');
        } else if (this.questHasMatchingRelation(quest)) {
          tick.classList.add('quest-card__tick--search-related');
        }
        if (this.userProgress.completedQuests.includes(quest.id)) {
          tick.classList.add('completed');
        }
        tick.dataset.questId = quest.id;
        tick.dataset.questIndex = String(index);

        // Add hover for popover
        tick.addEventListener('mouseenter', (e) => this.showQuestPopover(quest, e));
        tick.addEventListener('mouseleave', () => this.hideQuestPopover());

        // Click to toggle only this quest
        tick.addEventListener('click', () => {
          const completedSet = new Set(this.userProgress.completedQuests);
          if (completedSet.has(quest.id)) {
            completedSet.delete(quest.id);
          } else {
            // Completing a quest also completes every prerequisite in its relation chain.
            const previousQuestIds = this.getAllPreviousQuestIds(quest.id);
            previousQuestIds.forEach(id => completedSet.add(id));
            completedSet.add(quest.id);
          }
          this.userProgress.completedQuests = [...completedSet];
          StorageManager.saveUserProgress(this.userProgress);

          // Re-render to ensure prerequisite completions are reflected across all trader groups.
          this.initializeQuestTracker();

          this.allItems = this.decisionEngine.getItemsWithDecisions(this.userProgress);
          this.searchEngine.updateIndex(this.allItems);
          this.applyFilters();
          this.updateStats();
        });

        ticksContainer.appendChild(tick);
      });

      questTracker.appendChild(card);
    }

    // Show empty state if no quests
    if (sortedTraders.length === 0 || visibleQuests.length === 0) {
      questTracker.innerHTML = `
        <div class="quest-tracker__empty">
          <p>No quest data available</p>
          <p class="quest-tracker__empty-hint">Quest data will appear here once loaded</p>
        </div>
      `;
    }

    this.updateQuestSearchHighlights();
  }

  private buildQuestRelations(quests: Quest[]): void {
    const validIds = new Set(quests.map(quest => quest.id));
    const previousMap = new Map<string, Set<string>>();
    const nextMap = new Map<string, Set<string>>();

    for (const quest of quests) {
      previousMap.set(quest.id, new Set<string>());
      nextMap.set(quest.id, new Set<string>());
    }

    for (const quest of quests) {
      const questId = quest.id;
      const previousIds = Array.isArray(quest.previousQuestIds) ? quest.previousQuestIds : [];
      const nextIds = Array.isArray(quest.nextQuestIds) ? quest.nextQuestIds : [];

      for (const previousId of previousIds) {
        if (!validIds.has(previousId)) continue;
        previousMap.get(questId)?.add(previousId);
        nextMap.get(previousId)?.add(questId);
      }

      for (const nextId of nextIds) {
        if (!validIds.has(nextId)) continue;
        nextMap.get(questId)?.add(nextId);
        previousMap.get(nextId)?.add(questId);
      }
    }

    const sortByQuestOrder = (a: string, b: string): number => {
      const qa = quests.find(q => q.id === a);
      const qb = quests.find(q => q.id === b);
      if (!qa && !qb) return 0;
      if (!qa) return 1;
      if (!qb) return -1;
      return this.compareQuests(qa, qb);
    };

    this.questPreviousRelations = new Map(
      [...previousMap.entries()].map(([id, ids]) => [id, [...ids].sort(sortByQuestOrder)])
    );
  }

  private orderQuestsByRelations(quests: Quest[]): Quest[] {
    const questById = new Map(quests.map(quest => [quest.id, quest]));
    const ids = new Set(quests.map(quest => quest.id));
    const indegree = new Map<string, number>();
    const outgoing = new Map<string, string[]>();

    for (const quest of quests) {
      indegree.set(quest.id, 0);
      outgoing.set(quest.id, []);
    }

    for (const quest of quests) {
      // Use transitive previous relations so visible quests are still ordered correctly
      // when hidden/non-item quests exist between two visible nodes.
      const previousIds = this.getAllPreviousQuestIds(quest.id);
      for (const previousId of previousIds) {
        if (!ids.has(previousId)) continue;
        indegree.set(quest.id, (indegree.get(quest.id) || 0) + 1);
        const list = outgoing.get(previousId);
        if (list && !list.includes(quest.id)) {
          list.push(quest.id);
        }
      }
    }

    const queue = quests
      .filter(quest => (indegree.get(quest.id) || 0) === 0)
      .sort((a, b) => this.compareQuests(a, b));

    const ordered: Quest[] = [];

    while (queue.length > 0) {
      const current = queue.shift()!;
      ordered.push(current);

      const dependents = outgoing.get(current.id) || [];
      for (const dependentId of dependents) {
        const nextValue = (indegree.get(dependentId) || 0) - 1;
        indegree.set(dependentId, nextValue);
        if (nextValue === 0) {
          const dependentQuest = questById.get(dependentId);
          if (dependentQuest) queue.push(dependentQuest);
        }
      }

      queue.sort((a, b) => this.compareQuests(a, b));
    }

    // Guard against cycles or malformed relations by appending any remaining quests.
    if (ordered.length < quests.length) {
      const orderedIds = new Set(ordered.map(quest => quest.id));
      const remaining = quests
        .filter(quest => !orderedIds.has(quest.id))
        .sort((a, b) => this.compareQuests(a, b));
      ordered.push(...remaining);
    }

    return ordered;
  }

  private getAllPreviousQuestIds(questId: string): string[] {
    const visited = new Set<string>();
    const stack = [...(this.questPreviousRelations.get(questId) || [])];

    while (stack.length > 0) {
      const currentId = stack.pop()!;
      if (visited.has(currentId)) continue;
      visited.add(currentId);

      const previousOfCurrent = this.questPreviousRelations.get(currentId) || [];
      for (const previousId of previousOfCurrent) {
        if (!visited.has(previousId)) {
          stack.push(previousId);
        }
      }
    }

    return [...visited];
  }

  private compareQuests(a: Quest, b: Quest): number {
    const aOrder = a.sortOrder ?? Number.MAX_SAFE_INTEGER;
    const bOrder = b.sortOrder ?? Number.MAX_SAFE_INTEGER;

    if (aOrder !== bOrder) {
      return aOrder - bOrder;
    }

    return a.name.localeCompare(b.name);
  }

  private getProjectMaxPhase(project: Project): number {
    if (project.phases && project.phases.length > 0) {
      const maxFromPhases = Math.max(...project.phases.map(p => Number(p.phase) || 0));
      return Math.max(1, maxFromPhases);
    }

    if (project.requirements && project.requirements.length > 0) {
      return 1;
    }

    return 0;
  }

  private getProjectPhasesForUI(project: Project): Array<{ phase: number; name?: string; requirementItemIds?: Array<{ item_id: string; quantity: number }> }> {
    if (project.phases && project.phases.length > 0) {
      return [...project.phases]
        .map(phase => ({
          phase: Number(phase.phase) || 1,
          name: phase.name,
          requirementItemIds: phase.requirementItemIds || []
        }))
        .sort((a, b) => a.phase - b.phase);
    }

    if (project.requirements && project.requirements.length > 0) {
      return [{
        phase: 1,
        name: 'Requirements',
        requirementItemIds: project.requirements
      }];
    }

    return [];
  }

  private migrateProjectPhaseProgress() {
    let changed = false;

    for (const project of this.gameData.projects) {
      const maxPhase = this.getProjectMaxPhase(project);
      if (maxPhase <= 0) {
        continue;
      }

      const current = this.userProgress.projectPhaseProgress[project.id] || 0;
      if (this.userProgress.completedProjects.includes(project.id) && current < maxPhase) {
        this.userProgress.projectPhaseProgress[project.id] = maxPhase;
        changed = true;
      }
    }

    if (changed) {
      StorageManager.saveUserProgress(this.userProgress);
    }
  }

  private initializeProjectTracker() {
    const projectTracker = document.getElementById('project-tracker');
    if (!projectTracker) return;

    const projects = [...this.gameData.projects].sort((a, b) => a.name.localeCompare(b.name));

    for (const project of projects) {
      const phases = this.getProjectPhasesForUI(project);
      const maxPhase = Math.max(1, this.getProjectMaxPhase(project));
      const rawPhase = this.userProgress.projectPhaseProgress[project.id] || 0;
      const currentPhase = Math.min(maxPhase, Math.max(0, rawPhase));

      const card = document.createElement('div');
      card.className = 'project-card';

      const phaseText = currentPhase >= maxPhase
        ? `Phase ${currentPhase} / ${maxPhase} (Complete)`
        : `Phase ${currentPhase} / ${maxPhase}`;

      card.innerHTML = `
        <h3>${project.name}</h3>
        <div class="project-card__level">
          <span class="project-card__level-text">${phaseText}</span>
          <div class="project-card__ticks"></div>
        </div>
      `;

      const levelText = card.querySelector('.project-card__level-text') as HTMLSpanElement;
      const ticksContainer = card.querySelector('.project-card__ticks') as HTMLDivElement;

      const updateProgress = (newPhase: number) => {
        const normalized = Math.max(0, Math.min(maxPhase, newPhase));
        const nextText = normalized >= maxPhase
          ? `Phase ${normalized} / ${maxPhase} (Complete)`
          : `Phase ${normalized} / ${maxPhase}`;
        levelText.textContent = nextText;

        ticksContainer.querySelectorAll('.project-card__tick').forEach((tick, idx) => {
          tick.classList.toggle('completed', idx < normalized);
        });

        this.updateProjectProgress(project.id, normalized, maxPhase);
      };

      for (let phase = 1; phase <= maxPhase; phase++) {
        const tick = document.createElement('div');
        tick.className = 'project-card__tick';
        if (phase <= currentPhase) {
          tick.classList.add('completed');
        }

        const phaseData = phases.find(p => p.phase === phase);
        tick.addEventListener('mouseenter', (e) => this.showProjectPopover(project, phase, phaseData, e));
        tick.addEventListener('mouseleave', () => this.hideProjectPopover());

        tick.addEventListener('click', () => {
          const currentlyCompleted = tick.classList.contains('completed');
          const newValue = currentlyCompleted ? phase - 1 : phase;
          updateProgress(newValue);
        });

        ticksContainer.appendChild(tick);
      }

      projectTracker.appendChild(card);
    }

    if (projects.length === 0) {
      projectTracker.innerHTML = `
        <div class="quest-tracker__empty">
          <p>No project data available</p>
          <p class="quest-tracker__empty-hint">Project data will appear here once loaded</p>
        </div>
      `;
    }
  }

  private showProjectPopover(project: Project, phase: number, phaseData: any, event: MouseEvent) {
    this.hideProjectPopover();

    const popover = document.createElement('div');
    popover.className = 'quest-popover';
    popover.id = 'project-popover';

    let requirementsHtml = '<span class="quest-popover__none">None</span>';
    if (phaseData?.requirementItemIds && phaseData.requirementItemIds.length > 0) {
      requirementsHtml = phaseData.requirementItemIds.map((req: any) => {
        const itemId = req.item_id || req.itemId;
        const quantity = req.quantity || '?';
        const item = this.gameData.items.find(i => i.id === itemId);
        const itemName = item?.name || itemId || 'Unknown';
        const iconUrl = item ? dataLoader.getIconUrl(item) : '';

        return `
          <div class="quest-popover__item">
            ${iconUrl ? `<img src="${iconUrl}" alt="" class="quest-popover__item-icon" />` : ''}
            <span>${quantity}x ${itemName}</span>
          </div>
        `;
      }).join('');
    }

    popover.innerHTML = `
      <div class="quest-popover__title">${project.name} - Phase ${phase}</div>
      ${phaseData?.name ? `<div class="quest-popover__desc">${phaseData.name}</div>` : ''}
      <div class="quest-popover__section">
        <div class="quest-popover__section-title">Requirements</div>
        <div class="quest-popover__items">${requirementsHtml}</div>
      </div>
    `;

    document.body.appendChild(popover);

    const rect = (event.target as HTMLElement).getBoundingClientRect();
    const popoverRect = popover.getBoundingClientRect();

    let left = rect.left + rect.width / 2 - popoverRect.width / 2;
    let top = rect.top - popoverRect.height - 8;

    if (left < 8) left = 8;
    if (left + popoverRect.width > window.innerWidth - 8) {
      left = window.innerWidth - popoverRect.width - 8;
    }
    if (top < 8) {
      top = rect.bottom + 8;
    }

    popover.style.left = `${left}px`;
    popover.style.top = `${top}px`;
  }

  private hideProjectPopover() {
    const existing = document.getElementById('project-popover');
    if (existing) {
      existing.remove();
    }
  }

  private updateProjectProgress(projectId: string, phase: number, maxPhase: number) {
    this.userProgress.projectPhaseProgress[projectId] = phase;

    if (phase >= maxPhase) {
      if (!this.userProgress.completedProjects.includes(projectId)) {
        this.userProgress.completedProjects.push(projectId);
      }
    } else {
      this.userProgress.completedProjects = this.userProgress.completedProjects.filter(id => id !== projectId);
    }

    StorageManager.updateProjectPhaseProgress(projectId, phase, maxPhase);

    this.allItems = this.decisionEngine.getItemsWithDecisions(this.userProgress);
    this.searchEngine.updateIndex(this.allItems);

    this.applyFilters();
    this.updateStats();
  }

  private showQuestPopover(quest: any, event: MouseEvent) {
    // Remove existing popover if any
    this.hideQuestPopover();

    const popover = document.createElement('div');
    popover.className = 'quest-popover';
    popover.id = 'quest-popover';

    const fallbackIconSvg = '<svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20"><rect width="20" height="20" rx="2" fill="#212a2e"/><text x="10" y="14" text-anchor="middle" font-family="Arial,sans-serif" font-size="14" fill="#b0bec5">?</text></svg>';
    const fallbackIconUrl = `data:image/svg+xml,${encodeURIComponent(fallbackIconSvg)}`;
    const renderItemIcon = (iconUrl: string): string => {
      if (!iconUrl) {
        return `<img src="${fallbackIconUrl}" alt="" class="quest-popover__item-icon" />`;
      }

      return `<img src="${iconUrl}" alt="" class="quest-popover__item-icon" onerror="this.onerror=null;this.src='${fallbackIconUrl}'" />`;
    };

    // Build requirements HTML
    let requirementsHtml = '<span class="quest-popover__none">None</span>';
    if (quest.requirements && quest.requirements.length > 0) {
      requirementsHtml = quest.requirements.map((req: any) => {
        const itemId = req.item_id || req.itemId;
        const quantity = req.quantity || '?';
        const item = this.gameData.items.find(i => i.id === itemId);
        const itemName = item?.name || itemId || 'Unknown';
        const iconUrl = item ? dataLoader.getIconUrl(item) : '';

        return `
          <div class="quest-popover__item">
            ${renderItemIcon(iconUrl)}
            <span>${quantity}x ${itemName}</span>
          </div>
        `;
      }).join('');
    }

    // Build rewards HTML
    let rewardsHtml = '<span class="quest-popover__none">None</span>';
    const rewards: string[] = [];

    if (quest.xp) {
      rewards.push(`<div class="quest-popover__item"><span>${quest.xp} XP</span></div>`);
    }

    if (quest.rewards) {
      if (quest.rewards.item_id || quest.rewards.itemId) {
        const itemId = quest.rewards.item_id || quest.rewards.itemId;
        const quantity = quest.rewards.quantity || 1;
        const item = this.gameData.items.find(i => i.id === itemId);
        const itemName = item?.name || itemId || 'Unknown';
        const iconUrl = item && typeof item.imageFilename === 'string' && item.imageFilename.trim().length > 0
          ? dataLoader.getIconUrl(item)
          : '';

        rewards.push(`
          <div class="quest-popover__item">
            ${renderItemIcon(iconUrl)}
            <span>${quantity}x ${itemName}</span>
          </div>
        `);
      }

      // Handle array of rewards
      if (Array.isArray(quest.rewards)) {
        quest.rewards.forEach((reward: any) => {
          const itemId = reward.item_id || reward.itemId;
          const quantity = reward.quantity || 1;
          const item = this.gameData.items.find(i => i.id === itemId);
          const itemName = item?.name || itemId || 'Unknown';
          const iconUrl = item && typeof item.imageFilename === 'string' && item.imageFilename.trim().length > 0
            ? dataLoader.getIconUrl(item)
            : '';

          rewards.push(`
            <div class="quest-popover__item">
              ${renderItemIcon(iconUrl)}
              <span>${quantity}x ${itemName}</span>
            </div>
          `);
        });
      }
    }

    if (rewards.length > 0) {
      rewardsHtml = rewards.join('');
    }

    const renderRelationHtml = (relationIds: unknown): string => {
      if (!Array.isArray(relationIds) || relationIds.length === 0) {
        return '<span class="quest-popover__none">None</span>';
      }

      return relationIds
        .filter((id): id is string => typeof id === 'string' && id.length > 0)
        .map((id) => {
          const relatedQuest = this.gameData.quests.find((q) => q.id === id);
          const label = relatedQuest?.name || id;
          return `<div class="quest-popover__item"><span>${label}</span></div>`;
        })
        .join('');
    };

    const previousHtml = renderRelationHtml(quest.previousQuestIds);
    const unlocksHtml = renderRelationHtml(quest.nextQuestIds);

    popover.innerHTML = `
      <div class="quest-popover__title">${quest.name}</div>
      ${quest.description ? `<div class="quest-popover__desc">${quest.description}</div>` : ''}
      <div class="quest-popover__section">
        <div class="quest-popover__section-title">Requirements</div>
        <div class="quest-popover__items">${requirementsHtml}</div>
      </div>
      <div class="quest-popover__section">
        <div class="quest-popover__section-title">Rewards</div>
        <div class="quest-popover__items">${rewardsHtml}</div>
      </div>
      <div class="quest-popover__section">
        <div class="quest-popover__section-title">Previous</div>
        <div class="quest-popover__items">${previousHtml}</div>
      </div>
      <div class="quest-popover__section">
        <div class="quest-popover__section-title">Unlocks</div>
        <div class="quest-popover__items">${unlocksHtml}</div>
      </div>
    `;

    document.body.appendChild(popover);

    // Position the popover near the mouse
    const rect = (event.target as HTMLElement).getBoundingClientRect();
    const popoverRect = popover.getBoundingClientRect();

    let left = rect.left + rect.width / 2 - popoverRect.width / 2;
    let top = rect.top - popoverRect.height - 8;

    // Keep popover within viewport
    if (left < 8) left = 8;
    if (left + popoverRect.width > window.innerWidth - 8) {
      left = window.innerWidth - popoverRect.width - 8;
    }
    if (top < 8) {
      top = rect.bottom + 8;
    }

    popover.style.left = `${left}px`;
    popover.style.top = `${top}px`;
  }

  private hideQuestPopover() {
    const existing = document.getElementById('quest-popover');
    if (existing) {
      existing.remove();
    }
  }

  private initializeQuestTrackerToggle() {
    const button = document.getElementById('quest-non-item-toggle') as HTMLButtonElement | null;
    const searchInput = document.getElementById('quest-search-input') as HTMLInputElement | null;
    if (!button) {
      return;
    }

    const updateButtonState = () => {
      const isActive = this.showNonItemQuestNodes;
      button.classList.toggle('inactive', !isActive);
      button.setAttribute('aria-pressed', isActive ? 'true' : 'false');
      button.textContent = 'Non-Item Quests';
    };

    updateButtonState();

    if (searchInput) {
      searchInput.value = this.questSearchQuery;
      searchInput.addEventListener('input', (event) => {
        this.questSearchQuery = (event.target as HTMLInputElement).value;
        this.updateQuestSearchHighlights();
      });
    }

    button.addEventListener('click', () => {
      this.showNonItemQuestNodes = !this.showNonItemQuestNodes;
      StorageManager.saveQuestShowNonItem(this.showNonItemQuestNodes);
      updateButtonState();
      this.initializeQuestTracker();
    });
  }

  private questMatchesSearch(quest: Quest): boolean {
    const query = this.questSearchQuery.trim().toLowerCase();
    if (!query) {
      return false;
    }

    return quest.name.toLowerCase().includes(query) || quest.id.toLowerCase().includes(query);
  }

  private questHasMatchingRelation(quest: Quest): boolean {
    const query = this.questSearchQuery.trim().toLowerCase();
    if (!query) {
      return false;
    }

    const relatedIds = [
      ...(Array.isArray(quest.previousQuestIds) ? quest.previousQuestIds : []),
      ...(Array.isArray(quest.nextQuestIds) ? quest.nextQuestIds : [])
    ];

    return relatedIds.some((id) => {
      if (typeof id !== 'string') {
        return false;
      }

      if (id.toLowerCase().includes(query)) {
        return true;
      }

      const relatedQuest = this.gameData.quests.find((entry) => entry.id === id);
      return relatedQuest ? relatedQuest.name.toLowerCase().includes(query) : false;
    });
  }

  private updateQuestSearchHighlights() {
    const ticks = document.querySelectorAll('.quest-card__tick');

    ticks.forEach((tick) => {
      const element = tick as HTMLElement;
      const questId = element.dataset.questId;
      if (!questId) {
        element.classList.remove('quest-card__tick--search-match');
        return;
      }

      const quest = this.gameData.quests.find((entry) => entry.id === questId);
      const isDirectMatch = quest ? this.questMatchesSearch(quest) : false;
      const isRelatedMatch = quest ? this.questHasMatchingRelation(quest) : false;

      element.classList.toggle('quest-card__tick--search-match', isDirectMatch);
      element.classList.toggle('quest-card__tick--search-related', !isDirectMatch && isRelatedMatch);
    });
  }

  private hasQuestItemRequirements(quest: Quest): boolean {
    if (!Array.isArray(quest.requirements) || quest.requirements.length === 0) {
      return false;
    }

    return quest.requirements.some((requirement) => {
      const itemId = requirement.item_id;
      return typeof itemId === 'string' && itemId.length > 0;
    });
  }

  private hasQuestRequirementImage(quest: Quest): boolean {
    if (!Array.isArray(quest.requirements) || quest.requirements.length === 0) {
      return false;
    }

    return quest.requirements.some((requirement) => {
      const itemId = requirement.item_id;
      if (typeof itemId !== 'string' || itemId.length === 0) {
        return false;
      }

      const item = this.gameData.items.find((entry) => entry.id === itemId);
      if (!item) {
        return false;
      }

      return typeof item.imageFilename === 'string' && item.imageFilename.trim().length > 0;
    });
  }

  private applyFilters() {
    let items = [...this.allItems];
    const searchQuery = this.filters.searchQuery.trim();

    if (searchQuery) {
      // Search overrides all other filters so users can find any matching item quickly.
      items = this.searchEngine.search(searchQuery);
    } else {
      // Decision filter
      if (this.filters.decisions.size > 0) {
        items = items.filter(item =>
          this.filters.decisions.has(item.decisionData.decision)
        );
      }

      // Rarity filter
      if (this.filters.rarities.size > 0) {
        items = items.filter(item =>
          item.rarity && this.filters.rarities.has(item.rarity.toLowerCase())
        );
      }

      // Category filter (three-state: include takes priority over exclude)
      // Uses normalized categories for case-insensitive and fuzzy matching
      if (this.filters.categories.size > 0) {
        const included = [...this.filters.categories.entries()]
          .filter(([_, state]) => state === 'include')
          .map(([cat]) => cat);
        const excluded = [...this.filters.categories.entries()]
          .filter(([_, state]) => state === 'exclude')
          .map(([cat]) => cat);

        if (included.length > 0) {
          // Include mode: show ONLY included categories
          items = items.filter(item => included.includes(this.getItemCategory(item)));
        } else if (excluded.length > 0) {
          // Exclude mode: hide excluded categories
          items = items.filter(item => !excluded.includes(this.getItemCategory(item)));
        }
      }

      // Zone filter
      if (this.filters.zones.length > 0) {
        items = items.filter(item => {
          if (!item.foundIn || item.foundIn.length === 0) return false;
          // Item must be found in at least one of the selected zones
          return item.foundIn.some(zone => this.filters.zones.includes(zone));
        });
      }
    }

    // Sort items
    items = this.sortItems(items);

    this.filteredItems = items;

    // Update zone filter with current items (for count display)
    if (this.zoneFilter) {
      this.zoneFilter.updateItems(items);
    }

    this.render();
  }

  private sortItems(items: SearchableItem[]): SearchableItem[] {
    const rarityOrder = { legendary: 5, epic: 4, rare: 3, uncommon: 2, common: 1 };
    const decisionOrder = { keep: 3, situational: 2, sell_or_recycle: 1 };
    const direction = this.filters.sortDirection === 'asc' ? 1 : -1;

    return [...items].sort((a, b) => {
      let result = 0;

      switch (this.filters.sortBy) {
        case 'name':
          const nameA = a.name || '';
          const nameB = b.name || '';
          result = nameA.localeCompare(nameB);
          break;

        case 'value':
          result = (a.value || 0) - (b.value || 0);
          break;

        case 'rarity':
          const rarityA = rarityOrder[a.rarity?.toLowerCase() as keyof typeof rarityOrder] || 0;
          const rarityB = rarityOrder[b.rarity?.toLowerCase() as keyof typeof rarityOrder] || 0;
          result = rarityA - rarityB;
          break;

        case 'weight':
          result = (a.weightKg || 0) - (b.weightKg || 0);
          break;

        case 'decision':
          const decisionA = decisionOrder[a.decisionData.decision as keyof typeof decisionOrder] || 0;
          const decisionB = decisionOrder[b.decisionData.decision as keyof typeof decisionOrder] || 0;
          result = decisionA - decisionB;
          break;

        case 'type':
          result = (a.type || '').localeCompare(b.type || '');
          break;

        default:
          result = 0;
      }

      return result * direction;
    });
  }

  private render() {
    this.itemsGrid.innerHTML = '';

    if (this.filteredItems.length === 0) {
      this.itemsGrid.innerHTML = '<div class="no-results">No items found matching your filters.</div>';
      return;
    }

    this.filteredItems.forEach(item => {
      const itemCard = new ItemCard({
        item,
        decisionData: item.decisionData,
        onClick: (clickedItem) => this.showItemModal(clickedItem)
      });

      this.itemsGrid.appendChild(itemCard.render());
    });
  }

  private showItemModal(item: Item) {
    const itemWithDecision = this.allItems.find(i => i.id === item.id);
    if (!itemWithDecision) return;

    const modal = new ItemModal({
      item,
      decisionData: itemWithDecision.decisionData,
      decisionEngine: this.decisionEngine,
      onClose: () => { }
    });

    modal.show();
  }

  private updateStats() {
    const stats = this.decisionEngine.getDecisionStats(this.userProgress);

    this.updateStatElement('keep-count', stats.keep);
    this.updateStatElement('sell-or-recycle-count', stats.sell_or_recycle);
    this.updateStatElement('situational-count', stats.situational);
  }

  private updateStatElement(id: string, count: number) {
    const element = document.getElementById(id);
    if (element) {
      element.textContent = `${count} item${count !== 1 ? 's' : ''}`;
    }
  }

  private updateLastUpdated() {
    const element = document.getElementById('last-update');
    if (element && this.gameData.metadata) {
      const date = new Date(this.gameData.metadata.lastUpdated);
      element.textContent = date.toLocaleString();
    }
  }

  private showLoading() {
    const app = document.getElementById('app');
    if (app) {
      app.classList.add('loading');
    }
  }

  private hideLoading() {
    const app = document.getElementById('app');
    if (app) {
      app.classList.remove('loading');
    }
  }

  private showError(message: string) {
    const app = document.getElementById('app');
    if (app) {
      app.innerHTML = `
        <div class="error-screen">
          <h1>Error</h1>
          <p>${message}</p>
          <button onclick="location.reload()">Reload Page</button>
        </div>
      `;
    }
  }
}

// Bootstrap with PvP gate check
async function bootstrap() {
  const REDDIT_URL = 'https://www.reddit.com/r/ArcRaiders/';
  const answer = StorageManager.getPvPGateAnswer();

  // Already answered 4-5: redirect
  if (answer !== null && answer >= 4) {
    window.location.href = REDDIT_URL;
    return;
  }

  // Already answered 1-3: proceed
  if (answer !== null && answer <= 3) {
    new App().init();
    return;
  }

  // No answer: show modal
  const selected = await showPvPGateModal();
  StorageManager.setPvPGateAnswer(selected);

  if (selected >= 4) {
    window.location.href = REDDIT_URL;
  } else {
    new App().init();
  }
}

bootstrap();

// Sidebar tab switching functionality
document.addEventListener('DOMContentLoaded', () => {
  const tabs = document.querySelectorAll('.sidebar-tab');
  const panels = document.querySelectorAll('.sidebar-panel');
  const STORAGE_KEY = 'arc-raiders-sidebar-tab';

  // Helper to activate a tab
  const activateTab = (tabName: string) => {
    tabs.forEach(t => {
      t.classList.toggle('active', (t as HTMLElement).dataset.tab === tabName);
    });
    panels.forEach(panel => {
      panel.classList.toggle('active', panel.id === `panel-${tabName}`);
    });
  };

  // Load saved tab or default to 'hideout'
  const savedTab = localStorage.getItem(STORAGE_KEY) || 'hideout';
  activateTab(savedTab);

  // Tab click handlers
  tabs.forEach(tab => {
    tab.addEventListener('click', () => {
      const targetTab = (tab as HTMLElement).dataset.tab;
      if (targetTab) {
        activateTab(targetTab);
        localStorage.setItem(STORAGE_KEY, targetTab);
      }
    });
  });
});
